import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Line } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { getIsProgrammaticLineLevelChange } from 'store/dealManagement/selectors';
import { getProgrammaticLinesWithCurrent } from 'store/dealManagement/selectors/getProgrammaticLinesWithCurrent';
import useHeaderLinesLayout from './useHeaderLinesLayout';

export type UseProgrammaticHeaderLineContainer = {
  headerContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  displayDealLinesDropdown: boolean;
  displayedLines: Line[];
  allLinesSaved?: boolean;
  hasReachedMaxLines?: boolean;
};

export const useProgrammaticHeaderLineContainer = (): UseProgrammaticHeaderLineContainer => {
  const lines = useSelector(getProgrammaticLinesWithCurrent);
  const currentLine = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine);
  const maxAllowedLinesPerDeal = useSelector((state: Store) => state.dealConfig.maxAllowedLinesPerDeal);
  const isLineLevelChange = useSelector(getIsProgrammaticLineLevelChange);

  const [displayDealLinesDropdown, setDisplayDealLinesDropdown] = useState<boolean>(false);
  const [displayedLines, setDisplayedLines] = useState<Line[]>(lines);
  const allLinesSaved = useMemo(
    () => lines.every((line) => !!line.lineId) && !isLineLevelChange,
    [lines, isLineLevelChange],
  );
  const hasReachedMaxLines = useMemo(() => lines.length >= maxAllowedLinesPerDeal, [lines, maxAllowedLinesPerDeal]);

  const headerContainerRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  useHeaderLinesLayout({
    headerContainerRef,
    lines,
    currentLineId: currentLine.lineId,
    setDisplayedLines,
    setDisplayDealLinesDropdown,
  });

  return {
    headerContainerRef,
    displayDealLinesDropdown,
    displayedLines,
    allLinesSaved,
    hasReachedMaxLines,
  };
};
